import React, { useState, useEffect } from 'react'
import { Article } from '../../types'
import { Badge } from '../ui'
import { formatNumber } from '../../utils/formatters'
import { dashboardApi, articleApi } from '../../services/api'
import { useWebSocket } from '../../hooks/useWebSocket'

interface ArticlesListProps {
  articles: Article[]
  isLoading: boolean
  onExpandArticle?: (articleId: string) => void
  onSummarizeArticle?: (articleId: string) => void
  onPushToHubspot?: (articleId: string) => void
  sessionId?: string | null
  progressData?: {
    message: string
    progress: number
    current_page: number
    total_pages: number
    articles_found: number
  }
  pagination?: {
    current_page: number
    page_size: number
    total_count: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
  currentPage?: number
  onPageChange?: (page: number) => void
}

export const ArticlesList: React.FC<ArticlesListProps> = ({
  articles,
  isLoading,
  onExpandArticle,
  onSummarizeArticle,
  onPushToHubspot,
  sessionId,
  progressData,
  pagination,
  currentPage = 1,
  onPageChange
}) => {
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set())
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)
  const [loadingArticleDetails, setLoadingArticleDetails] = useState(false)
  const [loadingActions, setLoadingActions] = useState<Set<string>>(new Set())
  const [batchProcessing, setBatchProcessing] = useState(false)
  const [batchStatus, setBatchStatus] = useState<any>(null)
  const [selectAllPages, setSelectAllPages] = useState(false)
  const [allArticleIds, setAllArticleIds] = useState<string[]>([])
  const { lastMessage } = useWebSocket()

  // Handle WebSocket messages for batch progress and article updates
  useEffect(() => {
    if (lastMessage?.type === 'batch_progress') {
      const progress = lastMessage
      
      // Only update if this is for our current batch
      if (batchStatus?.batch_session_id === progress.batch_session_id) {
        setBatchStatus((prevStatus: any) => ({
          ...prevStatus,
          ...progress,
          progress_percentage: progress.total > 0 ? (progress.completed / progress.total) * 100 : 0
        }))
        
        // Stop processing state when completed
        if (progress.status === 'completed' || progress.status === 'error') {
          setBatchProcessing(false)
          if (progress.status === 'completed') {
            setSelectedArticles(new Set()) // Clear selection on success
          }
        }
      }
    } else if (lastMessage?.type === 'article_status_update') {
      // Handle individual article status updates during batch processing
      const { article_id, status: newStatus } = lastMessage
      
      // Trigger a refresh of the articles list to show updated statuses
      // This will cause the parent component to re-fetch articles
      if (onPageChange) {
        onPageChange(currentPage) // Refresh current page
      }
    } else if (lastMessage?.type === 'articles_updated') {
      // Handle batch completion - refresh the entire article list
      if (onPageChange) {
        onPageChange(currentPage) // Refresh current page to show all status updates
      }
    }
  }, [lastMessage, batchStatus?.batch_session_id, onPageChange, currentPage])

  // Helper function to extract original article date from raw_json
  const getArticleDate = (article: Article): string => {
    try {
      // First check if we have raw_json with the original date
      if ((article as any).raw_json) {
        const rawData = JSON.parse((article as any).raw_json)
        if (rawData.date && rawData.date !== 'null') {
          const articleDate = new Date(rawData.date)
          return articleDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
        }
      }
    } catch (error) {
      console.error('Error parsing article date:', error)
    }
    
    // Fallback to fetched_at if no original date available
    return new Date(article.fetched_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleSelectArticle = (articleId: string, checked: boolean) => {
    const newSelected = new Set(selectedArticles)
    if (checked) {
      newSelected.add(articleId)
    } else {
      newSelected.delete(articleId)
    }
    setSelectedArticles(newSelected)
  }

  const fetchAllArticleIds = async () => {
    if (!sessionId) return []
    
    try {
      // Fetch all pages to get all article IDs
      const allIds: string[] = []
      let currentPage = 1
      let hasMore = true
      
      while (hasMore) {
        const response = await dashboardApi.getArticlesBySession(sessionId, currentPage, 100) // Use larger page size
        allIds.push(...response.data.map((article: Article) => article.article_id))
        
        hasMore = response.pagination.has_next
        currentPage++
      }
      
      return allIds
    } catch (error) {
      console.error('Failed to fetch all article IDs:', error)
      return []
    }
  }

  const handleSelectAll = async (checked: boolean) => {
    if (checked) {
      if (selectAllPages) {
        // Select all articles across all pages
        if (allArticleIds.length === 0) {
          const ids = await fetchAllArticleIds()
          setAllArticleIds(ids)
          setSelectedArticles(new Set(ids))
        } else {
          setSelectedArticles(new Set(allArticleIds))
        }
      } else {
        // Select only current page articles
        setSelectedArticles(new Set(articles.map(a => a.article_id)))
      }
    } else {
      setSelectedArticles(new Set())
    }
  }

  const handleSelectAllPagesToggle = async () => {
    const newSelectAllPages = !selectAllPages
    setSelectAllPages(newSelectAllPages)
    
    if (newSelectAllPages && allArticleIds.length === 0) {
      // Fetch all article IDs when switching to "all pages" mode
      const ids = await fetchAllArticleIds()
      setAllArticleIds(ids)
    }
    
    // Clear current selection when switching modes
    setSelectedArticles(new Set())
  }

  const handleProcessSelected = async () => {
    if (selectedArticles.size === 0) return
    
    setBatchProcessing(true)
    setBatchStatus(null)
    
    try {
      const articleIds = Array.from(selectedArticles)
      const response = await articleApi.processSelectedArticles(articleIds)
      
      if (response.success) {
        setBatchStatus({
          batch_session_id: response.batch_session_id,
          status: 'processing',
          total_articles: response.article_count,
          completed: 0,
          failed: 0,
          progress_percentage: 0
        })
        
        // Poll for status updates
        pollBatchStatus(response.batch_session_id)
      }
    } catch (error: any) {
      console.error('Failed to start batch processing:', error)
      setBatchProcessing(false)
      // You might want to show an error notification here
    }
  }

  const pollBatchStatus = async (batchSessionId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await articleApi.getBatchStatus(batchSessionId)
        setBatchStatus(status)
        
        if (status.status === 'completed' || status.status === 'error') {
          clearInterval(pollInterval)
          setBatchProcessing(false)
          setSelectedArticles(new Set()) // Clear selection after completion
        }
      } catch (error) {
        console.error('Failed to get batch status:', error)
        clearInterval(pollInterval)
        setBatchProcessing(false)
      }
    }, 2000) // Poll every 2 seconds
    
    // Clean up interval after 5 minutes max
    setTimeout(() => {
      clearInterval(pollInterval)
      setBatchProcessing(false)
    }, 300000)
  }

  const handleArticleClick = async (article: Article) => {
    setLoadingArticleDetails(true)
    try {
      // Fetch full article details including parsed raw_json
      const fullArticle = await dashboardApi.getArticleById(article.article_id)
      setSelectedArticle(fullArticle)
    } catch (error) {
      console.error('Failed to load article details:', error)
      // Fallback to the article data we already have
      setSelectedArticle(article)
    } finally {
      setLoadingArticleDetails(false)
    }
  }

  // Enhanced action handlers with loading states
  const handleActionWithLoading = async (
    action: (articleId: string) => void | Promise<void>,
    articleId: string
  ) => {
    const newLoading = new Set(loadingActions)
    newLoading.add(articleId)
    setLoadingActions(newLoading)

    try {
      await action(articleId)
    } finally {
      const updatedLoading = new Set(loadingActions)
      updatedLoading.delete(articleId)
      setLoadingActions(updatedLoading)
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pushed_to_hubspot': return 'success'
      case 'summarised': return 'processing'
      case 'expanded': return 'info'
      case 'failed_expansion':
      case 'failed_summary':
      case 'failed_push': return 'error'
      default: return 'default'
    }
  }

  const formatStatus = (status: string) => {
    switch (status) {
      case 'pushed_to_hubspot': return 'In HubSpot'
      case 'summarised': return 'Analyzed'
      case 'expanded': return 'Expanded'
      case 'fetched': return 'Fetched'
      default: return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  const getArticleContent = (article: Article) => {
    if (article.expanded_description) {
      return article.expanded_description
    }
    if (article.description) {
      return article.description
    }
    if (article.full_content) {
      return article.full_content
    }
    if (article.content) {
      return article.content
    }
    if (article.text) {
      return article.text
    }
    if (article.excerpt) {
      return article.excerpt
    }
    return 'No content available'
  }

  const getPDFUrl = (article: Article) => {
    try {
      if (article.pdf_iframe_url) {
        return article.pdf_iframe_url
      }
      
      // Try to extract PDF URL from raw_json
      if ((article as any).raw_json) {
        const rawData = JSON.parse((article as any).raw_json)
        if (rawData.pdf_iframe_url) {
          return rawData.pdf_iframe_url
        }
        if (rawData.link && rawData.link.includes('.pdf')) {
          return rawData.link
        }
      }
    } catch (error) {
      console.error('Error extracting PDF URL:', error)
    }
    return null
  }

  const getSourceUrl = (article: Article) => {
    try {
      // Try to get URL from raw_json first
      if ((article as any).raw_json) {
        const rawData = JSON.parse((article as any).raw_json)
        if (rawData.link) {
          return rawData.link
        }
      }
      
      // Fallback to article.url if available
      if (article.url) {
        return article.url
      }
    } catch (error) {
      console.error('Error extracting source URL:', error)
    }
    
    // Ultimate fallback to MiningHub
    return `https://app.mininghub.com/article/${article.article_id}`
  }

  // Pagination helper component
  const PaginationControls = () => {
    if (!pagination || !onPageChange || pagination.total_pages <= 1) return null

    const handlePrevPage = () => {
      if (pagination.has_prev) {
        onPageChange(currentPage - 1)
      }
    }

    const handleNextPage = () => {
      if (pagination.has_next) {
        onPageChange(currentPage + 1)
      }
    }

    return (
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center text-sm text-gray-700">
          <span>
            Showing {((currentPage - 1) * pagination.page_size) + 1} to {Math.min(currentPage * pagination.page_size, pagination.total_count)} of {formatNumber(pagination.total_count)} articles
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handlePrevPage}
            disabled={!pagination.has_prev}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          
          <div className="flex items-center space-x-1">
            <span className="text-sm text-gray-700">
              Page {currentPage} of {pagination.total_pages}
            </span>
          </div>
          
          <button
            onClick={handleNextPage}
            disabled={!pagination.has_next}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    )
  }

  // Show empty state if no session has been created yet
  if (!sessionId) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Articles</h2>
        </div>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-3">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
          <p className="text-gray-500 mb-2">No articles yet</p>
          <p className="text-sm text-gray-400">Set your filters and click "Fetch News" to load articles</p>
        </div>
      </div>
    )
  }

  if (isLoading && articles.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading articles...</p>
          {progressData && (
            <div className="mt-4 max-w-md mx-auto">
              <div className="text-sm text-gray-500 mb-2">
                {progressData.message}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressData.progress}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Page {progressData.current_page} of {progressData.total_pages} • {formatNumber(progressData.articles_found)} articles found
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (articles.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Articles</h2>
          <div className="text-sm text-gray-500">Session: {sessionId?.substring(0, 8)}...</div>
        </div>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-3">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-gray-500 mb-2">No articles found for this session</p>
          <p className="text-sm text-gray-400">Try adjusting your filters and fetching again</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">
              News Articles
              {articles.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({formatNumber(articles.length)} article{articles.length !== 1 ? 's' : ''})
                </span>
              )}
            </h2>
            
            {articles.length > 0 && (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectAllPages ? selectedArticles.size === allArticleIds.length : selectedArticles.size === articles.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-600">
                    Select All {selectAllPages ? `(${pagination?.total_count || 0} articles)` : `(${articles.length} on page)`}
                  </span>
                </div>
                
                {pagination && pagination.total_pages > 1 && (
                  <button
                    onClick={handleSelectAllPagesToggle}
                    className="text-xs text-primary-600 hover:text-primary-800 transition-colors border border-primary-200 px-2 py-1 rounded"
                  >
                    {selectAllPages ? 'Current Page Only' : 'All Pages'}
                  </button>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            {selectedArticles.size > 0 && (
              <>
                <span className="text-sm text-gray-600">
                  {selectedArticles.size} selected
                </span>
                <button
                  onClick={() => setSelectedArticles(new Set())}
                  className="text-sm text-primary-600 hover:text-primary-800 transition-colors"
                >
                  Clear selection
                </button>
                <button
                  onClick={handleProcessSelected}
                  disabled={batchProcessing}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {batchProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>Process Selected</span>
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Batch Processing Status */}
        {batchStatus && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-blue-900">
                Batch Processing {batchStatus.status === 'completed' ? 'Complete' : 'In Progress'}
              </h3>
              <span className="text-xs text-blue-700 font-mono">
                {batchStatus.batch_session_id}
              </span>
            </div>
            
            <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${batchStatus.progress_percentage || 0}%` }}
              ></div>
            </div>
            
            <div className="flex items-center justify-between text-xs text-blue-700">
              <span>
                {batchStatus.completed || 0} of {batchStatus.total_articles || 0} articles processed
              </span>
              <span>
                {batchStatus.failed > 0 && `${batchStatus.failed} failed`}
              </span>
            </div>
            
            {batchStatus.status === 'completed' && batchStatus.results && (
              <div className="mt-2 text-xs text-blue-700">
                ✅ {batchStatus.results.expanded} expanded, {batchStatus.results.analyzed} analyzed, {batchStatus.results.pushed_to_hubspot} pushed to HubSpot
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-600">
              Showing {((pagination.current_page - 1) * pagination.page_size) + 1} to {Math.min(pagination.current_page * pagination.page_size, pagination.total_count)} of {formatNumber(pagination.total_count)} articles
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange?.(pagination.current_page - 1)}
                disabled={!pagination.has_prev}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                Page {pagination.current_page} of {pagination.total_pages}
              </span>
              
              <button
                onClick={() => onPageChange?.(pagination.current_page + 1)}
                disabled={!pagination.has_next}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Progress indicator during loading */}
        {isLoading && progressData && (
          <div className="mt-4">
            <div className="text-sm text-gray-600 mb-2">
              {progressData.message}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressData.progress}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Page {progressData.current_page} of {progressData.total_pages} • {formatNumber(progressData.articles_found)} articles found
            </div>
          </div>
        )}
      </div>

      {/* Articles List */}
      <div className="divide-y divide-gray-100">
        {articles.map((article) => (
          <div key={article.article_id} className="p-6 hover:bg-gray-50 transition-colors">
            <div className="flex items-start space-x-4">
              {/* Checkbox */}
              <div className="flex items-center pt-1">
                <input
                  type="checkbox"
                  checked={selectedArticles.has(article.article_id)}
                  onChange={(e) => handleSelectArticle(article.article_id, e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 
                      className="text-lg font-semibold text-gray-900 hover:text-primary-600 cursor-pointer transition-colors leading-tight"
                      onClick={() => handleArticleClick(article)}
                    >
                      {article.title}
                    </h3>
                    <div className="flex items-center space-x-3 mt-2 text-sm text-gray-500">
                      <span className="font-medium">{article.source}</span>
                      <span>•</span>
                      <span>{getArticleDate(article)}</span>
                      <span>•</span>
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">ID: {article.article_id}</span>
                    </div>
                    
                    {(article.description || article.expanded_description) && (
                      <p className="mt-3 text-gray-700 text-sm leading-relaxed line-clamp-3">
                        {article.expanded_description || article.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center space-x-3 ml-6">
                    <Badge variant={getStatusBadgeVariant(article.status)}>
                      {formatStatus(article.status)}
                    </Badge>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-3">
                    {article.status === 'fetched' && onExpandArticle && (
                      <button 
                        onClick={() => handleActionWithLoading(onExpandArticle, article.article_id)}
                        disabled={loadingActions.has(article.article_id)}
                        className="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {loadingActions.has(article.article_id) ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                            <span>Expanding...</span>
                          </>
                        ) : (
                          <span>Expand</span>
                        )}
                      </button>
                    )}
                    {article.status === 'expanded' && onSummarizeArticle && (
                      <button 
                        onClick={() => handleActionWithLoading(onSummarizeArticle, article.article_id)}
                        disabled={loadingActions.has(article.article_id)}
                        className="px-4 py-2 text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {loadingActions.has(article.article_id) ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-700"></div>
                            <span>Summarizing...</span>
                          </>
                        ) : (
                          <span>Summarize</span>
                        )}
                      </button>
                    )}
                    {article.status === 'summarised' && onPushToHubspot && (
                      <button 
                        onClick={() => handleActionWithLoading(onPushToHubspot, article.article_id)}
                        disabled={loadingActions.has(article.article_id)}
                        className="px-4 py-2 text-sm font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {loadingActions.has(article.article_id) ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                            <span>Pushing...</span>
                          </>
                        ) : (
                          <span>Push to HubSpot</span>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <a 
                    href={getSourceUrl(article)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-800 transition-colors font-medium"
                  >
                    View Source →
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      <PaginationControls />

      {/* Article Detail Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {selectedArticle.title}
                  </h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{selectedArticle.source}</span>
                    <span>•</span>
                    <span>{new Date(selectedArticle.fetched_at).toLocaleString()}</span>
                    <span>•</span>
                    <Badge variant={getStatusBadgeVariant(selectedArticle.status)}>
                      {formatStatus(selectedArticle.status)}
                    </Badge>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedArticle(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingArticleDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  <span className="ml-2 text-gray-500">Loading article details...</span>
                </div>
              ) : (
                <div>
                  <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="text-gray-700 font-medium">
                      {selectedArticle.status === 'expanded' ? '✓ Expanded Content' : 
                       selectedArticle.article_type === 'pdf' ? '📄 PDF Document' : 
                       'Content Preview'}
                    </span>
                    {selectedArticle.status === 'fetched' && (
                      <span className="text-gray-600 text-sm ml-2">Click "Expand" to see full content</span>
                    )}
                  </div>
                  
                  {/* PDF iframe if available */}
                  {getPDFUrl(selectedArticle) && (
                    <iframe 
                      src={getPDFUrl(selectedArticle)!} 
                      className="w-full h-96 border border-gray-300 rounded-lg mb-4"
                      title="PDF Document"
                    />
                  )}
                  
                  {/* Article content */}
                  <div 
                    className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                    {...(getArticleContent(selectedArticle).includes('<') ? 
                      { dangerouslySetInnerHTML: { __html: getArticleContent(selectedArticle) } } : 
                      { children: getArticleContent(selectedArticle) }
                    )}
                  />
                </div>
              )}
              
              {selectedArticle.url && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <a 
                    href={selectedArticle.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800 transition-colors"
                  >
                    View Original Article →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 